import React from 'react';
import { format } from 'date-fns';
import { Printer } from 'lucide-react';
import signatureImage from '../images/signature.png';
import { formatIndianCurrency } from '../utils/numberToWords';
import { formatInvoiceNumber } from '../utils/invoiceNumber';

function AdhocInvoicePreview({
  client,
  invoiceNumber,
  invoiceDate,
  dueDate,
  placeOfSupply,
  items,
  subtotal,
  cgst,
  sgst,
  roundOff = 0,
  total,
  amountInWords,
  printRef,
  handlePrint,
  config,
  poNumber = '',
  poDate = '',
  showShipTo,
  financialYear,
  hideInlinePrintButton = false,
  referenceQuoteNumber,
  bankAccount = null, // Bank account stored in invoice
  paymentTerm = '',
  purchaseInvoiceNumber = ''
}) {
  // Use bankAccount from invoice if provided, otherwise use config/client logic
  let bankDetails;
  if (bankAccount && bankAccount.bankName) {
    // Use bank account stored in invoice
    bankDetails = bankAccount;
  } else {
    // Fallback to config/client logic
    const selectedBank = config.bankAccounts?.find(bank => bank.id === client?.bankAccountId);
    bankDetails = selectedBank || config.bankAccounts?.[0] || {
      bankName: 'Bank Name',
      accountName: 'Account Name',
      accountNumber: 'Account Number',
      ifscCode: 'IFSC Code',
      branch: 'Branch'
    };
  }

  // Helper for N/A display
  const displayOrNA = (val) => (val && String(val).trim() !== '' ? val : 'N/A');

  // Helper for safe date formatting
  const formatDate = (dateString) => {
    if (!dateString || dateString === '') return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return format(date, 'dd-MMM-yy');
    } catch (error) {
      console.error('Date formatting error:', error, 'for date:', dateString);
      return 'N/A';
    }
  };

  // Helper for safe invoice number formatting
  const formatInvoiceNumberSafe = (invNo, fy, cfg) => {
    try {
      // If already formatted (contains a year prefix like 2025-26/) just return as-is
      if (typeof invNo === 'string' && /\d{4}-\d{2}\/\d+/.test(invNo)) {
        return invNo;
      }
      if (!fy || typeof fy !== 'string') {
        return invNo || 'N/A';
      }
      return formatInvoiceNumber(invNo, fy, cfg);
    } catch (error) {
      console.error('Invoice number formatting error:', error);
      return invNo || 'N/A';
    }
  };

  // Calculate item totals for adhoc invoice
  const calculateItemTotals = (item) => {
    // Support alternate field names just in case
    const qtyRaw = item.qty ?? item.quantity ?? 1;
    const qty = Number(qtyRaw) || 1;
    const taxRate = Number(item.taxPercent ?? item.tax ?? 0) || 0;

    // Compute net amount from unit price when available; otherwise fallback to stored fields
    let netAmount = NaN;
    const explicitUnitPrice = Number(item.unitPrice ?? item.rate ?? item.price ?? item.unit_price);
    if (!isNaN(explicitUnitPrice) && explicitUnitPrice > 0) {
      netAmount = explicitUnitPrice * qty;
    }
    if (isNaN(netAmount)) {
      const storedNet = Number(item.netAmount ?? item.amount);
      if (!isNaN(storedNet) && storedNet > 0) {
        netAmount = storedNet;
      } else {
        const totalFromItem = Number(item.totalAmount);
        netAmount = (!isNaN(totalFromItem) && totalFromItem > 0)
          ? totalFromItem / (1 + taxRate / 100)
          : 0;
      }
    }

    // Derive unit price from net amount and qty if not explicitly present
    let unitPrice = Number(item.unitPrice ?? item.rate ?? item.price ?? item.unit_price);
    if (isNaN(unitPrice) || unitPrice <= 0) {
      unitPrice = qty > 0 ? netAmount / qty : 0;
    }

    // Always compute tax and total from current values to avoid stale stored zeros
    const taxAmount = netAmount * (taxRate / 100);
    const totalAmount = netAmount + taxAmount;

    return { unitPrice, netAmount, taxAmount, totalAmount, taxRate };
  };

  return (
    <div className="invoice-preview" ref={printRef} style={{
      position: 'relative',
      width: '100%',
      maxWidth: 'none',
      overflow: 'visible',
      border: '1px solid #000 !important'
    }}>
      {/* Watermark logo */}
      < img
        src="/spatium-logo-1.png"
        alt="Watermark"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '60%',
          opacity: 0.07,
          zIndex: 0,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      />
      {/* Main invoice content (zIndex: 1 by default) */}
      <div className="invoice-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', paddingTop: '20px', paddingBottom: '20px' }}>
        <img src="/spatium-logo-1.png" alt="Spatium Logo" style={{ maxWidth: '160px', height: 'auto', flex: '0 0 auto', padding: '10px' }} />
        <div style={{ textAlign: 'right', flex: 1, paddingRight: "10px" }}>
          <h2 className="invoice-title" style={{ marginBottom: 0 }}>{'TAX INVOICE'}</h2>
          <p style={{ marginTop: -1, marginBottom: 0 }}>Spatium Iconic, 4, 4-1, Bannerghatta Main Road,<br />JP Nagar Phase 3, Bengaluru, Karnataka, 560078</p>
          <p style={{ marginTop: 2, marginBottom: 0 }}><strong>GSTIN:</strong> {config?.gstin || 'GSTIN'}</p>
          {config?.udyamNo && (
            <p style={{ marginTop: 2, marginBottom: 0 }}><strong>UDYAM NO:</strong> {config.udyamNo}</p>
          )}
        </div>
      </div>

      <div className="invoice-details" style={{ display: 'flex', gap: '0px' }}>
        <div style={{ flex: '0 0 54.5%' }}>
          <div className="invoice-info" style={{ borderBottom: '0px' }}>
            <h3>Bill To:</h3>
            <p><strong>{client?.registeredName || client?.name || 'Client Name'}</strong></p>
            <p>{client?.billToAddress || client?.address || 'Client Address'}</p>
            <p>Phone: {client?.phone || 'N/A'}</p>
            <p>GSTIN: {client?.gstin || 'N/A'}</p>
          </div>
          {showShipTo === 'yes' && (
            <div className="invoice-info" style={{ borderBottom: '0px' }} >
              <h3>Ship To:</h3>
              <p><strong>{client?.registeredName || client?.name || 'Client Name'}</strong></p>
              <p>{client?.shipToAddress || client?.address || 'Client Address'}</p>
              <p>Phone: {client?.phone || 'N/A'}</p>
              <p>GSTIN: {client?.gstin || 'N/A'}</p>
            </div>
          )}
        </div>
        <div style={{ flex: '0 0 45.5%' }}>
          <table className="invoice-meta-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: 400, width: '50%', borderRight: '1px solid #333' }}>Invoice No.<br /><span style={{ fontWeight: 700 }}>{formatInvoiceNumberSafe(invoiceNumber, financialYear, config)}</span></td>
                <td style={{ fontWeight: 400 }}>Dated<br /><span style={{ fontWeight: 700 }}>{formatDate(invoiceDate)}</span></td>
              </tr>
              <tr>
                <td style={{ borderRight: '1px solid #333' }}>Due Date<br /><span style={{ fontWeight: 700 }}>{formatDate(dueDate)}</span></td>
                <td>Payment Terms<br /><span style={{ fontWeight: 700 }}>{displayOrNA(paymentTerm)}</span></td>
              </tr>
              <tr>
                <td style={{ borderRight: '1px solid #333' }}>PO Number<br /><span style={{ fontWeight: 700 }}>{displayOrNA(poNumber)}</span></td>
                <td>PO Date<br /><span style={{ fontWeight: 700 }}>{formatDate(poDate)}</span></td>
              </tr>
              <tr>
                <td style={{ borderRight: '1px solid #333', paddingBottom: '6px', borderBottom: '0px' }}>Quote Number<br /><span style={{ fontWeight: 700 }}>{displayOrNA(referenceQuoteNumber)}</span></td>
                <td style={{ paddingBottom: '3px', borderBottom: '0px' }}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <table className="invoice-table">
        <thead>
          <tr>
            <th style={{ width: '50px' }}>Sr.No.</th>
            <th style={{ width: '80px' }}>HAC/SAC</th>
            <th style={{ width: '300px' }}>Description</th>
            <th style={{ width: '100px' }}>Unit Price</th>
            <th style={{ width: '80px' }}>Qty</th>
            <th style={{ width: '110px' }}>UOM</th>
            <th style={{ width: '100px' }}>Net Amount</th>
            <th style={{ width: '80px' }}>Tax %</th>
            <th style={{ width: '100px' }}>Tax Amount</th>
            <th style={{ width: '100px' }}>Total Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const { unitPrice, netAmount, taxAmount, totalAmount, taxRate } = calculateItemTotals(item);
            return (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.hsn}</td>
                <td>{item.description}</td>
                <td>{formatIndianCurrency(unitPrice)}</td>
                <td>{item.qty || 1}</td>
                <td>{item.uom || ''}</td>
                <td>{formatIndianCurrency(netAmount)}</td>
                <td>{taxRate}%</td>
                <td>{formatIndianCurrency(taxAmount)}</td>
                <td>{formatIndianCurrency(totalAmount)}</td>
              </tr>
            );
          })}

        </tbody>
      </table>

      {
        (() => {
          // Derive totals from items if incoming props are zero/undefined
          const derived = items.reduce((acc, it) => {
            const { netAmount, taxAmount } = calculateItemTotals(it);
            acc.net += netAmount;
            acc.tax += taxAmount;
            return acc;
          }, { net: 0, tax: 0 });
          // Use derived totals if stored totals are zero/undefined but items have valid values
          const storedSubtotal = Number(subtotal || 0);
          const storedTotal = Number(total || 0);
          const useDerived = (storedSubtotal === 0 || storedTotal === 0) && derived.net > 0;
          const effSubtotal = useDerived ? derived.net : storedSubtotal;
          const effCgst = useDerived ? derived.tax / 2 : Number(cgst || 0);
          const effSgst = useDerived ? derived.tax / 2 : Number(sgst || 0);
          const gross = effSubtotal + effCgst + effSgst;
          const effRoundOff = Number(roundOff || 0);
          const effTotal = gross + effRoundOff;
          return (
            <div className="invoice-total">
              <table>
                <tbody>
                  <tr>
                    <td style={{ borderBottom: '0px', borderRight: '0px' }}>Subtotal:</td>
                    <td style={{ borderBottom: '0px', width: '83px' }}>{formatIndianCurrency(effSubtotal)}</td>
                  </tr>
                  <tr>
                    <td style={{ borderBottom: '0px', borderRight: '0px' }}>CGST:</td>
                    <td style={{ borderBottom: '0px' }}>{formatIndianCurrency(effCgst)}</td>
                  </tr>
                  <tr>
                    <td style={{ borderBottom: '0px', borderRight: '0px' }}>SGST:</td>
                    <td style={{ borderBottom: '0px' }}>{formatIndianCurrency(effSgst)}</td>
                  </tr>
                  <tr>
                    <td style={{ borderBottom: '0px', borderRight: '0px' }}>Round Off:</td>
                    <td style={{ borderBottom: '0px' }}>{formatIndianCurrency(effRoundOff)}</td>
                  </tr>
                  <tr className="total-row">
                    <td style={{ borderRight: '0px' }}><strong>Total:</strong></td>
                    <td><strong>{formatIndianCurrency(useDerived ? effTotal : Number(total || effTotal))}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()
      }

      <div className="amount-in-words">
        <strong>Amount in Words:</strong> {amountInWords || 'Zero Rupees Only'}
      </div>

      <div className="invoice-footer" style={{
        marginTop: '40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        minHeight: '200px'
      }}>
        <div style={{
          flex: '1',
          marginRight: '20px',
          marginLeft: '10px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}>
          <div>
            <h4>Bank Details:</h4>
            {bankDetails && (
              <div>
                <p><strong>Bank:</strong> {bankDetails.bankName}</p>
                <p><strong>Account Name:</strong> {bankDetails.accountName}</p>
                <p><strong>Account Number:</strong> {bankDetails.accountNumber}</p>
                <p><strong>IFSC Code:</strong> {bankDetails.ifscCode}</p>
                <p><strong>Branch:</strong> {bankDetails.branch}</p>
              </div>
            )}
          </div>

          {/* Made with Ledgerly logo - positioned at bottom */}
          <div style={{
            marginTop: '90px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '0px'
          }}>
            <span style={{ fontSize: '12px', color: '#666' }}>Made with</span>
            <img
              src="/spatium-logo.png"
              alt="Ledgerly Logo"
              style={{
                width: '80px',
                height: '25px',
                objectFit: 'contain'
              }}
            />
            <span style={{ fontSize: '10px', color: '#666' }}>A product of Spatium Offices</span>
          </div>
        </div>
        <div style={{ flex: '1', textAlign: 'right', marginRight: '10px' }}>
          <h4>Authorized Signatory:</h4>
          <div style={{ marginTop: '20px', display: 'inline-block' }}>
            <img
              src={signatureImage}
              alt="Signature"
              style={{
                maxWidth: '150px',
                height: 'auto',
                marginBottom: '10px'
              }}
            />
            <p style={{ margin: '0', fontSize: '12px' }}>_____________________</p>
            <p style={{ margin: '5px 0 0 0', fontSize: '12px' }}>Signature</p>
          </div>
        </div>
      </div>

      {
        !hideInlinePrintButton && (
          <div style={{ marginTop: '40px', textAlign: 'center' }} className="no-print">
            <button
              onClick={handlePrint}
              style={{
                background: '#007bff',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
              className="print-button"
            >
              <Printer size="1em" />
              Print Invoice
            </button>
          </div>
        )
      }

      {/* PTO text for print - will only show in print */}
      <div className="pto-text" style={{
        position: 'fixed',
        bottom: '5mm',
        right: '20mm',
        fontSize: '12pt',
        fontWeight: 'bold',
        color: '#333',
        zIndex: 1000
      }}>
        PTO
      </div>
    </div >
  );
}

export default AdhocInvoicePreview; 